import React, { useState, useEffect } from 'react';
import { useAuth } from "../AuthContext";
import './Project.css';
import ConfirmPopup from './ConfirmPopup';
import ArrayEdit from "./ArrayEdit";


const Project = ({ selectedProject, userMessage, setUserMessage }) => {
    const { setIsAuthenticated } = useAuth();
    const [project, setProject] = useState(null);
    const [units, setUnits] = useState(null);
    const [schema, setSchema] = useState(null);
    const [showPopup, setShowPopup] = useState(false);
    const [isLoading, setIsLoading] = useState(false);


    const fetchProjectDetails = async () => {
        setIsLoading(true); // Set loading to true when request starts
        setUserMessage(''); // Clear any previous messages
        const apiUrl = process.env.REACT_APP_API_URL;
        const token = localStorage.getItem('token');

        try {
            const response = await fetch(`${apiUrl}/users/get-project`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `${token}`,
                },
                body: JSON.stringify({ _id: selectedProject.id })
            });

            if (response.status === 401) {
                setIsAuthenticated(false);
            } else {
                const data = await response.json();
                if (response.ok && data.success === true) {
                    setProject(data.project);
                    setUnits(data.project.units);
                    setSchema(data.project.schema);
                } else {
                    setUserMessage('Failed to retrieve project from database.');
                    setProject(null);
                    setUnits(null);
                    setSchema(null);
                }
            }
        } catch (error) {
            setUserMessage('Error fetching project details');
            setProject(null);
            setUnits(null);
            setSchema(null);
            setIsAuthenticated(false);
        } finally {
            setIsLoading(false); // Set loading to false when request completes
        }
    };

    useEffect(() => {
        fetchProjectDetails();
    }, [selectedProject.id]);

    const handleInputChange = (unitIndex, key, value) => {
        const updatedUnits = Object.values(units).map((unit, index) => {
            if (index === unitIndex) {
                return { ...unit, [key]: value };
            }
            return unit;
        });
        setUnits(updatedUnits);
    };

    const confirmSubmit = async (e) => {
        setShowPopup(false);
        e.preventDefault();
        const apiUrl = process.env.REACT_APP_API_URL;
        const token = localStorage.getItem('token');

        try {
            const response = await fetch(`${apiUrl}/users/update-project`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `${token}`,
                },
                body: JSON.stringify({ _id: selectedProject.id, units }),
            });
            if (response.ok) {
                setUserMessage('Project updated successfully [' + formatDateNow() + ']');
            } else {
                console.error('Failed to update project');
            }
        } catch (error) {
            console.error('Error updating project:', error);
        }

    };

    const cancelSubmit = async (e) => {
        setShowPopup(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setShowPopup(true); // Show the confirmation popup
    };

    const formatDateNow = () => {
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    };




    // inside your component
    const [showArrayEdit, setShowArrayEdit] = useState(false);
    const [editFieldKey, setEditFieldKey] = useState(null);
    const [editFieldUnitIndex, setEditFieldUnitIndex] = useState(null);

    const handleOpenArrayEdit = (unitIndex, key) => {
        setEditFieldUnitIndex(unitIndex);
        setEditFieldKey(key);
        setShowArrayEdit(true);
    };

    const handleArrayEditSubmit = (newArray) => {
        handleInputChange(editFieldUnitIndex, editFieldKey, newArray);
        setShowArrayEdit(false);
    };

    const handleArrayEditCancel = () => {
        setShowArrayEdit(false);
    };






    const renderInput = (unitIndex, key, value) => {
        if (!schema) return null;
        //console.log(key, 'type: ', (typeof value), 'isArray: ', Array.isArray(value));

        // Handle undefined or null values
        if (value === undefined || value === null) {
            return <div className="empty-value">-</div>;
        }

        if(key === "name") {
            return (
                <div className="non-editable-input">
                    {value.toLocaleString()} {/* Format the value with commas */}
                </div>
            );

        }

        if (Array.isArray(schema[key]) && schema[key].length === 0) {
            const currentValue = value || [];

            return (
                <div>
                    <button onClick={(event) => {
                        event.preventDefault();
                        handleOpenArrayEdit(unitIndex, key);
                    }}>
                        Edit List ({currentValue.length} items)
                    </button>


                </div>
            );
        } else if (typeof value === 'number') {
            const { min, max, step } = schema[key] || { min: 0, max: 10000000, step: 1 };
            return (
                <input
                    type="number"
                    value={value}
                    min={min}
                    max={max}
                    step={step}
                    onChange={(e) => handleInputChange(unitIndex, key, parseFloat(e.target.value))}
                />
            );
        } else if (typeof value === 'string') {
            if (schema[key] && Array.isArray(schema[key])) {
                const options = schema[key];
                //console.log('LOGGING OPTIONS: ', options);
                //console.log('KEY & VALUE: ', key, value);
                return (
                    <select value={value} onChange={(e) => handleInputChange(unitIndex, key, e.target.value)}>
                        {options.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                );
            }
            return (
                <input
                    type="text"
                    value={value}
                    onChange={(e) => handleInputChange(unitIndex, key, e.target.value)}
                />
            );
        }
        return null;
    };

    return (
        <div className="project-container">
            {isLoading ? (
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p className="loading-text">Loading project details...</p>
                </div>
            ) : project ? (
                <div className="project-content">
                    <h1 className="project-title">{project.name}</h1>
                    <p className="project-description">{project.description}</p>
                    {units && Object.keys(units).length > 0 ? (
                        <form onSubmit={handleSubmit}>
                            <div className="table-container">
                                <table className="project-table">
                                    <thead>
                                    <tr>
                                        {schema && Object.keys(schema).map((key, columnIndex) => (
                                            <th key={key} className={`project-table-header ${columnIndex === 0 ? 'sticky-column' : ''}`}>
                                                {key}
                                            </th>
                                        ))}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {Object.values(units).map((unit, unitIndex) => (
                                        <tr key={unitIndex}>
                                            {schema && Object.keys(schema).map((key) => (
                                                <td
                                                    key={key}
                                                    className={`project-table-cell ${key === 'name' ? 'sticky-column' : ''}`}
                                                >
                                                    {renderInput(unitIndex, key, unit[key])}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="save-button-container">
                                <button type="submit" className="submit-button">Save Changes</button>
                            </div>
                        </form>
                    ) : (
                        <p className="no-units-message">No units available.</p>
                    )}
                    {showPopup && (
                        <ConfirmPopup
                            message="Are you sure you want to save changes?"
                            onConfirm={confirmSubmit}
                            onCancel={cancelSubmit}
                        />
                    )}

                    {showArrayEdit && (
                        <ArrayEdit
                            initialArray={units[editFieldUnitIndex][editFieldKey]}
                            type="string" // or "number" — adjust as needed
                            onDone={(newArr)=>{
                                console.log(units[editFieldUnitIndex][editFieldKey], 'will become ', newArr);
                                units[editFieldUnitIndex][editFieldKey] = newArr;
                                setShowArrayEdit(false);
                            }}
                            onCancel={handleArrayEditCancel}
                        />
                    )}
                </div>
            ) : (
                <p className="error-message">Error retrieving project details.</p>
            )}
        </div>
    );

};

export default Project;
